import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Database, Hash } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const codeFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const PAGE_SIZE = 10;

function QdrantVectorPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let active = true;
    const loadPayloads = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${documentId}/qdrant-vectors`,
          { params: { limit: PAGE_SIZE, ...(cursor ? { offset: cursor } : {}) }, withCredentials: true }
        );
        if (active) setData(response.data);
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.message || 'Gagal memuat payload dari Qdrant.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadPayloads();
    return () => { active = false; };
  }, [documentId, cursor]);

  const showNext = () => {
    if (!data?.nextOffset) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(data.nextOffset);
    setPage((current) => current + 1);
  };

  const showPrevious = () => {
    if (!cursorHistory.length) return;
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory((history) => history.slice(0, -1));
    setCursor(previousCursor);
    setPage((current) => Math.max(1, current - 1));
  };

  const totalPages = data?.total ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="container-fluid py-4" style={{ fontFamily: codeFont }}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => navigate('/dashboard/manage/document')}>
            <ArrowLeft size={15} className="me-1" /> Kembali ke Dokumen
          </button>
          <h3 className="mb-1 d-flex align-items-center gap-2" style={{ fontFamily: codeFont }}>
            <Database size={24} /> Qdrant Payload Inspector
          </h3>
          <div className="text-muted small">Read-only view untuk document_id: {documentId}</div>
        </div>
        {data && (
          <div className="border rounded bg-light px-3 py-2 small text-end">
            <div>collection: <strong>{data.collection}</strong></div>
            <div>total chunks: <strong>{data.total || 0}</strong> · page: <strong>{page}{totalPages ? `/${totalPages}` : ''}</strong></div>
          </div>
        )}
      </div>

      {loading && <div className="alert alert-secondary">Memuat payload chunk dari Qdrant...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-body">
              <div className="row g-3 small">
                <div className="col-md-5"><span className="text-muted">document:</span> {data.document?.title || '-'}</div>
                <div className="col-md-4"><span className="text-muted">file:</span> {data.document?.file_name || '-'}</div>
                <div className="col-md-3"><span className="text-muted">chunks in Qdrant:</span> {data.total || 0}</div>
              </div>
            </div>
          </div>

          {data.points?.length === 0 ? (
            <div className="alert alert-warning">Belum ada point Qdrant untuk dokumen ini.</div>
          ) : data.points.map((point, index) => (
            <div className="card shadow-sm border-0 mb-3" key={point.id}>
              <div className="card-header bg-dark text-light px-3 py-3">
                <div className="d-flex align-items-center gap-2 fw-semibold">
                  <Hash size={16} />
                  <span>Chunk {(page - 1) * PAGE_SIZE + index + 1}</span>
                </div>
                <div className="mt-2 pt-2 border-top border-secondary text-white-50 small text-break" style={{ lineHeight: 1.5 }}>
                  <span className="me-2">point_id:</span>{point.id}
                </div>
              </div>
              <div className="card-body">
                <pre className="mb-0 p-3 rounded bg-light border" style={{ fontFamily: codeFont, fontSize: '12px', maxHeight: '520px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(point.payload || {}, null, 2)}
                </pre>
              </div>
            </div>
          ))}

          {data.total > PAGE_SIZE && (
            <div className="d-flex justify-content-between align-items-center border-top pt-3 pb-2">
              <button className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={loading || cursorHistory.length === 0} onClick={showPrevious}>
                <ChevronLeft size={16} /> Sebelumnya
              </button>
              <span className="small text-muted">Halaman {page} dari {totalPages}</span>
              <button className="btn btn-outline-primary d-inline-flex align-items-center gap-1" disabled={loading || !data.nextOffset} onClick={showNext}>
                Berikutnya <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default QdrantVectorPage;
